import { gql } from "@apollo/client";

// ===== ORDER MUTATIONS =====

// Create order mutation - updated to match GraphQL schema
export const CREATE_ORDER = gql`
  mutation CreateOrder($input: OrderCreateInput!) {
    orderCreate(input: $input) {
      id
      brandId
      pointId
      number
      type
      status
      comment
      priceTotal
      personsNumber
      items {
        id
        productId
        price
        quantity
        product {
          id
          name
          slug
          description
          images {
            url
            priority
          }
          priceSettings {
            price
          }
        }
      }
    }
  }
`;

// Update order mutation - new
export const UPDATE_ORDER = gql`
  mutation UpdateOrder($input: OrderUpdateInput!) {
    orderUpdate(input: $input) {
      id
      brandId
      pointId
      number
      type
      status
      comment
      priceTotal
      personsNumber
      items {
        id
        productId
        price
        quantity
        product {
          id
          name
          slug
          description
          images {
            url
            priority
          }
          priceSettings {
            price
          }
        }
      }
    }
  }
`;

// ===== ORDER QUERIES =====

// Get single order query - new
export const GET_ORDER = gql`
  query GetOrder($input: OrderInput!) {
    order(input: $input) {
      id
      brandId
      pointId
      number
      type
      status
      comment
      priceTotal
      personsNumber
      items {
        id
        productId
        price
        quantity
        product {
          id
          name
          slug
          description
          images {
            url
            priority
          }
          priceSettings {
            price
          }
        }
      }
    }
  }
`;

// Get orders list query - enhanced
export const GET_ORDERS = gql`
  query GetOrders($input: OrdersInput!) {
    orders(input: $input) {
      id
      brandId
      pointId
      number
      type
      status
      comment
      priceTotal
      personsNumber
      items {
        id
        productId
        price
        quantity
        product {
          id
          name
          slug
          description
          images {
            url
            priority
          }
          priceSettings {
            price
          }
        }
      }
    }
  }
`;

// ===== UTILITY FRAGMENTS =====

// Order item fragment for reuse
export const ORDER_ITEM_FRAGMENT = gql`
  fragment OrderItemDetails on OrderItem {
    id
    productId
    price
    quantity
    product {
      id
      name
      slug
      description
      images {
        url
        priority
      }
      priceSettings {
        price
      }
    }
  }
`;

// Full order fragment for reuse
export const ORDER_FRAGMENT = gql`
  fragment OrderDetails on Order {
    id
    brandId
    pointId
    number
    type
    status
    comment
    priceTotal
    personsNumber
    items {
      ...OrderItemDetails
    }
  }
  ${ORDER_ITEM_FRAGMENT}
`;

// ===== OPTIMIZED QUERIES WITH FRAGMENTS =====

// Create order with fragment
export const CREATE_ORDER_OPTIMIZED = gql`
  mutation CreateOrder($input: OrderCreateInput!) {
    orderCreate(input: $input) {
      ...OrderDetails
    }
  }
  ${ORDER_FRAGMENT}
`;

// Update order with fragment
export const UPDATE_ORDER_OPTIMIZED = gql`
  mutation UpdateOrder($input: OrderUpdateInput!) {
    orderUpdate(input: $input) {
      ...OrderDetails
    }
  }
  ${ORDER_FRAGMENT}
`;

// Get order with fragment
export const GET_ORDER_OPTIMIZED = gql`
  query GetOrder($input: OrderInput!) {
    order(input: $input) {
      ...OrderDetails
    }
  }
  ${ORDER_FRAGMENT}
`;

// Get orders with fragment
export const GET_ORDERS_OPTIMIZED = gql`
  query GetOrders($input: OrdersInput!) {
    orders(input: $input) {
      ...OrderDetails
    }
  }
  ${ORDER_FRAGMENT}
`;

// ===== SPECIALIZED QUERIES =====

// Get orders with minimal fields for list views
export const GET_ORDERS_LIST = gql`
  query GetOrdersList($input: OrdersInput!) {
    orders(input: $input) {
      id
      number
      type
      status
      priceTotal
      personsNumber
      items {
        id
        quantity
        product {
          id
          name
        }
      }
    }
  }
`;

// Get order status only (for polling)
export const GET_ORDER_STATUS = gql`
  query GetOrderStatus($input: OrderInput!) {
    order(input: $input) {
      id
      status
      priceTotal
    }
  }
`;

// ===== EXPORTED QUERIES FOR HOOKS =====

// Default exports for use in hooks
export const ORDER_MUTATIONS = {
  CREATE_ORDER: CREATE_ORDER_OPTIMIZED,
  UPDATE_ORDER: UPDATE_ORDER_OPTIMIZED,
} as const;

export const ORDER_QUERIES = {
  GET_ORDER: GET_ORDER_OPTIMIZED,
  GET_ORDERS: GET_ORDERS_OPTIMIZED,
  GET_ORDERS_LIST,
  GET_ORDER_STATUS,
} as const;

// Export all for direct usage if needed
export {
  CREATE_ORDER_OPTIMIZED as CREATE_ORDER_MUTATION,
  UPDATE_ORDER_OPTIMIZED as UPDATE_ORDER_MUTATION,
  GET_ORDER_OPTIMIZED as GET_ORDER_QUERY,
  GET_ORDERS_OPTIMIZED as GET_ORDERS_QUERY,
};
